'use client';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { Payload } from './simple-store-list';
import StoreCard from './store-card';
import { getStoresFn, type Store } from '@/api/ahafood/get-store-fn';
import { trackEvent } from '@/lib/event-tracking';
import { createSearchSession } from '@/lib/search-session';
import { useAuthStore } from '@/store';
import { useAiFoodOrderStore } from '@/store/use-ai-store';

type StoreListProps = {
  data: Store[];
  payload: Payload;
};

export default function StoreList({ data, payload }: StoreListProps) {
  const { user } = useAuthStore();
  const address = useAiFoodOrderStore.use.address();
  const lat = address?.lat;
  const lng = address?.lng;
  const limit = 20;

  // Add tracking ref for search events
  const lastLoggedQuery = useRef<string | undefined>(undefined);

  const {
    data: storesData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['stores', payload],
    queryFn: async ({ pageParam = 0 }) => {
      return getStoresFn({
        ...payload,
        limit,
        skip: pageParam,
        ...(lat && lng ? { lat, lng } : {}),
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.stores) return undefined;
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.stores?.length || 0),
        0
      );
      return lastPage.stores.length < limit ? undefined : loaded;
    },
    initialData: data?.length
      ? {
          pages: [{ stores: data, total: data.length }],
          pageParams: [0],
        }
      : undefined,
  });

  const stores = useMemo(() => {
    return (
      storesData?.pages?.flatMap((page) => page.stores).filter(Boolean) ?? []
    );
  }, [storesData?.pages]);

  // Apply filters using useMemo to avoid re-creating on every render
  const filteredStores = useMemo(() => {
    let result = stores;

    if (payload?.promotion) {
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.toLowerCase().includes('deal'))
      );
    }
    if (payload?.freeship === 'freeship') {
      // Filter for any freeship (50% or 100%)
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.toLowerCase().includes('freeship'))
      );
    } else if (payload?.freeship === 'freeship-100') {
      // Filter for specifically Freeship 100%
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.includes('Freeship 100%'))
      );
    }
    if (payload?.open_now) {
      result = result.filter((store) => store.is_temporary_closed === false);
    }

    return result;
  }, [stores, payload?.promotion, payload?.freeship, payload?.open_now]);

  // Log search event when payload.text changes and stores are loaded
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      payload.text &&
      payload.text !== lastLoggedQuery.current &&
      filteredStores.length > 0 &&
      user?.cunstomerId
    ) {
      setTimeout(() => {
        try {
          // Create search session and get search_id
          const searchId = createSearchSession(
            user.cunstomerId ?? '',
            payload.text ?? ''
          );

          // Create first 5 stores data with separate fields for GA compatibility
          const first5Stores = filteredStores.slice(0, 5);
          const eventData: Record<string, any> = {
            key_word: payload.text,
            user_id: user.cunstomerId,
            search_id: searchId,
            total_stores_found: filteredStores.length,
          };

          // Add each store as separate fields to avoid GA 100 character limit
          first5Stores.forEach((store, index) => {
            const position = index + 1;
            const storeItemIds =
              store.highlight_items
                ?.slice(0, 3)
                .map((item) => item._id)
                .join(',') ?? '';

            eventData[`first_store_${position}`] = JSON.stringify({
              s_id: store._id.toString(),
              p: position,
              i_id: storeItemIds,
            });
          });

          trackEvent('search_event', eventData);
          lastLoggedQuery.current = payload.text;
        } catch (e) {
          console.error('[StoreList] Error logging search results', e);
        }
      }, 0);
    }
  }, [payload.text, filteredStores, user?.cunstomerId]);

  return (
    <>
      <InfiniteScroll
        className={cn(
          'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6 lg:gap-8',
          'auto-rows-max items-start justify-items-stretch pb-20'
        )}
        dataLength={filteredStores.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<LoadingSkeleton />}
        endMessage={<p className="text-center"></p>}
      >
        {filteredStores.length > 0 ? (
          filteredStores.map((store, index) => (
            <StoreCard key={store._id} data={store} position={index + 1} />
          ))
        ) : (
          <p className="leading-7 [&:not(:first-child)]:mt-6">
            Không có cửa hàng nào.
          </p>
        )}
      </InfiniteScroll>
    </>
  );
}

function LoadingSkeleton() {
  return [...Array(4)].map((_, index) => (
    <div
      className="group relative col-span-1 flex h-full flex-row gap-2 rounded-xl sm:flex-col"
      key={index}
    >
      <Skeleton className="relative aspect-square w-24 flex-none overflow-hidden rounded-xl border border-neutral-15 bg-neutral-10 sm:aspect-[316/200] sm:w-full" />
      <div className="flex-1 overflow-hidden">
        <Skeleton className="h-5 w-4/6" />
        <Skeleton className="mt-1 h-4 w-1/2" />
      </div>
    </div>
  ));
}
